import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { Box } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { AnalyticValues } from "@/contexts/analytics/hooks";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { SheetActionRow } from "@/ui/Clickable/SheetActionRow";
import { InlineHint } from "@/ui/molecules/InlineHint";
import { SheetStep } from "@/ui/Popups/SheetStep";
import { SheetStepButton } from "@/ui/Popups/SheetStepButton";
import { formatPredictedCost } from "@/utils/costPrediction";
import { NotificationType, type OpenShiftRequest, type ReplaceShiftRequest } from "@/utils/models";
import { countNurseResponses } from "@/utils/utilFunctions";
import { joinLabelParts } from "../gapsLabels";
import { currentRequestBonus } from "../gapsPickupBonuses";
import { useGapsRequestLabels } from "../openShifts/useGapsRequestLabels";
import { GapsJobBadge } from "./GapsJobBadge";
import { GapsPostedAgencyRow } from "./GapsPostedAgencyRow";
import { GapsPostedDeleteRow } from "./GapsPostedDeleteRow";
import { GapsPostedRoboCallRow } from "./GapsPostedRoboCallRow";
import { GapsPostedStep, isIncentiveEditable } from "./gapsPostedSteps";

type Props = {
	request: OpenShiftRequest | ReplaceShiftRequest;
	/** The day the request is drawn on, for the heading ("Thu 1 Oct"). */
	dayLabel: string;
	onStep: (step: GapsPostedStep) => void;
	/**
	 * The request's staffing day is in a week the phone has loaded, so the home can open the add sheet on it. Outside
	 * them the home lands on the nearest loaded day instead and the sheet opened for the request's day closes, so the tap
	 * would silently do nothing: the row is off and says why.
	 */
	isRequestDayLoaded: boolean;
	onAddDirectly: () => void;
	onClose: () => void;
};

const SHEET = "gaps-posted";

/**
 * What you can do about a posted shift (P3-10), each row the existing hook the Requests screen already uses:
 * - Robo call (`GapsPostedRoboCallRow`) and Send to agency (`GapsPostedAgencyRow`), each with its own confirm;
 * - Pick-up incentive, Hours and Staff needed — their own steps (`useUpdateRequestFlatBonus`, `useUpdateRequestTime`,
 *   `useUpdateNursesNeeded`); an incentive that changes over time is shown but not editable (`isIncentiveEditable`);
 * - Add someone directly — the add sheet, LINKED to this request so the add fills one of its openings — off, with the
 *   reason, for a request on a day outside the weeks the phone has loaded (`isRequestDayLoaded`);
 * - Delete (`GapsPostedDeleteRow`), behind the same confirm the Requests screen asks.
 *
 * Replacements and swaps carry only the rows that apply to them (robo call, delete) — the classic card's own split.
 */
export const GapsPostedActionsStep: FC<Props> = ({ request, dayLabel, onStep, isRequestDayLoaded, onAddDirectly, onClose }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { sectionsOf, timeLabelOf, headingOf } = useGapsRequestLabels();
	const isShiftBonusesEnabled = useFeatureFlag(FEATURE_FLAGS.EnableShiftBonuses);

	const departmentId = chosenDepartment.department_id;
	const isOpenShift = request.request_type === NotificationType.OpenShift;
	// A request whose type is OpenShift IS an open-shift request; the union is not discriminated in the model types.
	const openShift = isOpenShift ? (request as OpenShiftRequest) : null;
	const [section] = useMemo(() => sectionsOf(request), [sectionsOf, request]);
	const responseCount = countNurseResponses(request);
	const properties = useMemo<AnalyticValues>(
		() => ({ department_id: departmentId, request_id: request.id, request_type: request.request_type }),
		[departmentId, request],
	);
	const isIncentiveLocked = !isIncentiveEditable(request);
	const bonus = currentRequestBonus(request);

	const subtitle = joinLabelParts([dayLabel, request.location, openShift === null ? undefined : t("gaps.requests.needed", { count: openShift.needed_nurses })]);

	return (
		<SheetStep
			stepKey={GapsPostedStep.Actions}
			dataTestId={SHEET}
			leading={section === undefined ? undefined : <GapsJobBadge group={section} dataTestId={`${SHEET}-job-badge`} />}
			title={headingOf(request)}
			subtitle={subtitle}
			footer={
				<SheetStepButton tone="dismiss" onClick={onClose} dataTestId={`${SHEET}-cancel`}>
					{t("close")}
				</SheetStepButton>
			}
		>
			<Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
				<InlineHint tone={responseCount > 0 ? "info" : "warning"} dataTestId={`${SHEET}-status`}>
					{responseCount > 0 ? t("gaps.postedSheet.statusAnswered", { count: responseCount }) : t("gaps.postedSheet.statusNoAnswers")}
				</InlineHint>
			</Box>

			<GapsPostedRoboCallRow request={request} properties={properties} testIdPrefix={SHEET} />
			<GapsPostedAgencyRow
				request={request}
				// An agency posting is named on an open shift only.
				agencyName={openShift === null ? undefined : openShift.agency_source_name}
				properties={properties}
				testIdPrefix={SHEET}
			/>

			{isOpenShift && isShiftBonusesEnabled && (
				<SheetActionRow
					icon={<PaidOutlinedIcon />}
					label={t("gaps.postedSheet.incentive")}
					description={isIncentiveLocked ? t("gaps.postedSheet.incentiveCurve") : undefined}
					trailing={bonus > 0 ? t("gaps.requests.bonus", { amount: formatPredictedCost(bonus) }) : t("gaps.postedSheet.incentiveNone")}
					showChevron={!isIncentiveLocked}
					disabled={isIncentiveLocked}
					onClick={() => onStep(GapsPostedStep.Incentive)}
					dataTestId={`${SHEET}-incentive`}
				/>
			)}
			{isOpenShift && (
				<SheetActionRow
					icon={<ScheduleOutlinedIcon />}
					label={t("gaps.postedSheet.hours")}
					trailing={timeLabelOf(request)}
					showChevron
					onClick={() => onStep(GapsPostedStep.Hours)}
					dataTestId={`${SHEET}-hours`}
				/>
			)}
			{openShift !== null && (
				<SheetActionRow
					icon={<GroupOutlinedIcon />}
					label={t("gaps.postedSheet.needed")}
					trailing={String(openShift.needed_nurses)}
					showChevron
					onClick={() => onStep(GapsPostedStep.Needed)}
					dataTestId={`${SHEET}-needed`}
				/>
			)}
			{isOpenShift && (
				<SheetActionRow
					icon={<PersonAddAltOutlinedIcon />}
					label={t("gaps.postedSheet.addDirectly")}
					description={isRequestDayLoaded ? t("gaps.postedSheet.addDirectlyHint") : t("gaps.postedSheet.addDirectlyTooFarAhead")}
					showChevron={isRequestDayLoaded}
					disabled={!isRequestDayLoaded}
					onClick={onAddDirectly}
					dataTestId={`${SHEET}-add-directly`}
				/>
			)}

			<GapsPostedDeleteRow request={request} properties={properties} onDeleted={onClose} testIdPrefix={SHEET} />
		</SheetStep>
	);
};
